import { useQuery } from '@tanstack/react-query';
import { Field, FieldArray, Form, Formik } from 'formik';
import { Select, TreeSelect } from 'antd';
import { Category } from '../../../models/Category/Category';
import { Filter } from '../../../models/Filter/Filter';
import { Attribute } from '../../../models/Attribute/Attribute';
import { Button, Col, FormGroup, FormLabel, Row } from 'react-bootstrap';
import { InputField } from '../../components/InputField/InputField';
import { ErrorMessageLabel } from '../../components/ErrorMessageLabel/ErrorMessageLabel';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import { ToastContainer, toast } from 'react-toastify';
import * as Yup from 'yup';
import style from './create-attribute.module.scss';
import { AttributeTypes } from '../../../Shop/components/Attributes/AttributeValue';
import { AttributeValueStorePayload } from '../../../models/Attribute/contracts';
import ColorBox from '../../components/ColorBox/ColorBox';

interface AttributeFilterValue {
  id: number;
  settings?: {
    score?: number;
  };
}

interface AttributeValueForm {
  value: string;
  filter_values: AttributeFilterValue[];
  settings?: any;
}

interface CreateAttributeValues {
  categories: number[];
  name: string;
  type: AttributeTypes;
  values: AttributeValueForm[];
}

function CreateAttribute() {
  const { data: filters } = useQuery(['filters'], () => Filter.all('values'));
  const { data: categories } = useQuery(['categories'], () => Category.all());
  const { refetch: refetchAttributes } = useQuery(['attributes'], () =>
    Attribute.all('values')
  );

  const initialValues: CreateAttributeValues = {
    categories: [],
    name: '',
    type: AttributeTypes.TEXT,
    values: []
  };

  return (
    <>
      <h1 className="cs-title">Kreiranje Atributa</h1>
      <Row>
        <Col lg={12}>
          <Formik<CreateAttributeValues>
            initialValues={initialValues}
            validationSchema={Yup.object({
              name: Yup.string().max(255, 'name.max').required('name.required'),
              type: Yup.string().required(),
              categories: Yup.array().of(Yup.number()).optional(),
              values: Yup.array()
                .min(1, 'Unesite bar jednu vrednost!')
                .of(
                  Yup.object().shape({
                    value: Yup.string().required('Vrednost je obavezna!'),
                    filter_values: Yup.array()
                      .of(
                        Yup.object().shape({
                          id: Yup.number().required(),
                          settings: Yup.object().optional()
                        })
                      )
                      .required(),
                    settings: Yup.mixed().optional()
                  })
                )
            })}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              setSubmitting(true);

              const mappedValues: AttributeValueStorePayload[] =
                values.values.map((val) => ({
                  value: val.value,
                  settings: val.settings ?? null,
                  filter_values: val.filter_values.map((fv) =>
                    typeof fv === 'object' && fv !== null ? fv.id : fv
                  ),
                  type: values.type // <-- Add this line!
                }));

              Attribute.store({
                ...values,
                presentation_type: values.type,
                values: mappedValues
              })
                .then(() => {
                  refetchAttributes();
                  resetForm();
                  toast.success('Atribut uspešno sačuvan!');
                })
                .catch((error) => {
                  toast.error(
                    error?.response?.data?.message || 'Greška pri snimanju.'
                  );
                })
                .finally(() => {
                  setSubmitting(false);
                });
            }}
          >
            {({ values, setFieldValue }) => (
              <Form autoComplete="off" className="cs-form-wrapper">
                <FormGroup>
                  <FormLabel>Izaberi Kategoriju</FormLabel>
                  <TreeSelect
                    treeCheckable
                    showSearch
                    treeDefaultExpandAll
                    placeholder="Izaberi kategoriju"
                    treeData={categories ?? []}
                    value={values.categories}
                    onChange={(value) =>
                      setFieldValue('categories', value, true)
                    }
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Naziv Atributa</FormLabel>
                  <InputField
                    placeholder="unesi ime atributa"
                    name="name"
                    autoComplete="off"
                  />
                  <ErrorMessageLabel name="name" />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Tip Prikaza Atributa</FormLabel>
                  <Field
                    placeholder="Izaberi"
                    name="type"
                    component={Select}
                    mode={'single'}
                    defaultValue={AttributeTypes.TEXT}
                    onChange={(value: AttributeTypes) =>
                      setFieldValue('type', value, true)
                    }
                  >
                    <Select.OptGroup>
                      <Select.Option value={AttributeTypes.TEXT}>
                        Tekst
                      </Select.Option>
                      <Select.Option value={AttributeTypes.COLOR}>
                        Boja
                      </Select.Option>
                      <Select.Option value={AttributeTypes.IMAGE}>
                        Slika
                      </Select.Option>
                    </Select.OptGroup>
                  </Field>
                  <ErrorMessageLabel name="type" />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Vrednost Atributa</FormLabel>
                  <FieldArray name="values">
                    {({ push, remove }) => (
                      <>
                        {values.values?.map((val, index) => (
                          <Row key={index} className="mb-3">
                            <Col className="cs-attribute-name-wrapper">
                              <InputField
                                placeholder={
                                  values.type === AttributeTypes.COLOR
                                    ? 'Unesite hex vrednost boje'
                                    : 'Unesite vrednost'
                                }
                                name={`values.${index}.value`}
                                autoComplete="off"
                                onChange={(event) => {
                                  const { value } = event.target;
                                  setFieldValue(`values.${index}.value`, value);
                                }}
                              />
                              {values.type === AttributeTypes.COLOR && (
                                <div className={style.colorPickerInput}>
                                  <ColorBox
                                    value={val.value}
                                    onChange={(color: any) =>
                                      setFieldValue(
                                        `values.${index}.settings`,
                                        color
                                      )
                                    }
                                  />
                                </div>
                              )}
                              <ErrorMessageLabel
                                className="cs-error-msg"
                                name={`values.${index}.value`}
                              />
                            </Col>
                            <Col className="cs-attribute-filters-wrapper">
                              <Field
                                placeholder="Izaberi"
                                name={`values.${index}.filter_values`}
                                component={Select}
                                mode="multiple"
                                onChange={(
                                  filterValueIds:
                                    | number[]
                                    | AttributeFilterValue[]
                                ) => {
                                  // Map to objects for form state (preserve settings)
                                  const selected: AttributeFilterValue[] = (
                                    filterValueIds as (
                                      | number
                                      | AttributeFilterValue
                                    )[]
                                  ).map((fid) => {
                                    if (
                                      typeof fid === 'object' &&
                                      fid !== null &&
                                      'id' in fid
                                    ) {
                                      return fid as AttributeFilterValue;
                                    }
                                    const existing = val.filter_values?.find(
                                      (fv) => fv.id === fid
                                    );
                                    if (existing) return existing;
                                    return { id: fid as number };
                                  });
                                  setFieldValue(
                                    `values.${index}.filter_values`,
                                    selected
                                  );
                                }}
                                value={val.filter_values?.map((fv) => fv.id)}
                              >
                                {filters?.map((f) =>
                                  !f.options ? null : (
                                    <Select.OptGroup key={f.id} label={f.name}>
                                      {f.options.map((opt) => (
                                        <Select.Option
                                          key={opt.id}
                                          value={opt.id}
                                        >
                                          {opt.value}
                                        </Select.Option>
                                      ))}
                                    </Select.OptGroup>
                                  )
                                )}
                              </Field>

                              <Button
                                className="JHover"
                                variant="outline-light"
                                onClick={() => remove(index)}
                              >
                                <i className="fa-solid fa-trash-can"></i>
                              </Button>
                            </Col>
                          </Row>
                        ))}
                        <Button
                          variant="outline-light"
                          className="btn-lg JHover"
                          onClick={() =>
                            push({
                              value: '',
                              filter_values: [],
                              settings: null
                            })
                          }
                        >
                          <i className="fa-solid fa-circle-plus"></i>
                        </Button>
                      </>
                    )}
                  </FieldArray>
                </FormGroup>
                <ButtonPrimary type="submit">Sačuvaj Atribut</ButtonPrimary>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </>
  );
}

export default CreateAttribute;
